import { useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import axios from 'axios';
import API_URL from '../config';
import logger from '../utils/logger';


const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes for inactivity timeout
const SESSION_PING_INTERVAL = 5 * 60 * 1000; // 5 minutes for session ping

const InactivityTracker = () => {
  const { logout } = useUser();
  const timeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Function to terminate session on the backend
  const terminateBackendSession = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        logger.debug(' InactivityTracker: Attempting to terminate backend session');
        // Attempt to terminate the session on the backend
        await axios.post(`${API_URL}/api/users/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000 // Increase timeout from 3000ms to 8000ms
        });
        logger.debug(' InactivityTracker: Backend session terminated successfully');
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        logger.error(' InactivityTracker: Failed to terminate backend session: timeout exceeded');
      } else {
        logger.error(' InactivityTracker: Failed to terminate backend session:', error);
      }
      // Continue with local logout even if backend call fails
    }
  };

  // Enhanced logout that terminates backend session
  const handleLogout = async () => {
    try {
      await terminateBackendSession();
    } finally {
      // Always perform local logout even if backend call fails
      logout();
    }
  };

  // Function to ping the server to keep the session alive
  const pingSession = async () => {
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 5000; // 5 seconds between retries
    
    const attemptPing = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          logger.debug(' InactivityTracker: No token found, skipping session ping');
          return;
        }
        
        logger.debug(' InactivityTracker: Pinging server to keep session alive');
        
        // Use the /users/info endpoint instead of /users/ping which doesn't exist
        // This endpoint is already used elsewhere in the app for token validation
        await axios.get(`${API_URL}/api/users/info`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000 // Increase timeout to 15 seconds
        });
        
        logger.debug(' InactivityTracker: Session ping successful');
      } catch (error) {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          logger.warn(` InactivityTracker: Session ping timed out (attempt ${retryCount + 1}/${maxRetries})`);
        } else if (error.response?.status === 401) {
          // Token is invalid or expired
          logger.warn(' InactivityTracker: Session ping failed - unauthorized. Token may be invalid.');
          // Don't retry for auth errors
          return;
        } else {
          logger.error(' InactivityTracker: Session ping failed:', error);
        }
        
        // Retry logic
        if (retryCount < maxRetries) {
          retryCount++;
          logger.debug(` InactivityTracker: Retrying ping in ${retryDelay/1000} seconds (${retryCount}/${maxRetries})`);
          
          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return attemptPing();
        } else {
          logger.error(` InactivityTracker: Session ping failed after ${maxRetries} attempts`);
          // After max retries, we don't log out immediately as it could be a temporary network issue
          // The user can continue working, and we'll try again on the next ping interval
        }
      }
    };
    
    await attemptPing();
  };

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      // Log out the user after inactivity timeout
      logger.debug(` InactivityTracker: User inactive for ${INACTIVITY_TIMEOUT/60000} minutes, logging out`);
      handleLogout();
      // Show notification to the user
      alert('You have been logged out due to inactivity. Please log in again.');
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    // List of events to track user activity
    const events = [
      'mousedown', 'mousemove', 'keypress', 
      'scroll', 'touchstart', 'click', 'wheel'
    ];

    // Initial timer setup
    resetTimer();

    // Set up ping interval to keep session alive while user is active
    pingIntervalRef.current = setInterval(pingSession, SESSION_PING_INTERVAL);

    // Set up event listeners for user activity
    const activityHandler = () => resetTimer();
    events.forEach(event => {
      window.addEventListener(event, activityHandler);
    });

    // Handle browser/tab closure
    const handleBeforeUnload = async (event) => {
      logger.debug(' InactivityTracker: Browser/tab closing, attempting to terminate session');
      
      // For modern browsers, we need to show a confirmation dialog
      // This gives us time to make the async call
      event.preventDefault();
      event.returnValue = '';
      
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Use sendBeacon for more reliable delivery during page unload
          const logoutEndpoint = `${API_URL}/api/users/logout`;
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          
          // Try sendBeacon first (more reliable during page unload)
          const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
          const beaconSuccess = navigator.sendBeacon(logoutEndpoint, blob);
          
          if (beaconSuccess) {
            logger.debug(' InactivityTracker: Session termination beacon sent successfully');
          } else {
            logger.debug(' InactivityTracker: Beacon failed, falling back to fetch API');
            // Fallback to fetch with keepalive
            try {
              await fetch(logoutEndpoint, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({}),
                keepalive: true // This helps ensure the request completes even as the page unloads
              });
              logger.debug(' InactivityTracker: Fetch with keepalive sent for session termination');
            } catch (fetchError) {
              logger.error(' InactivityTracker: Fetch fallback also failed:', fetchError);
            }
          }
        }
      } catch (error) {
        logger.error(' InactivityTracker: Error during session termination on page unload:', error);
      }
      
      // Clear local storage as a last resort
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      // Allow the page to unload after our code runs
      // Modern browsers will still show a confirmation dialog
      setTimeout(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }, 0);
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Add visibility change listener to detect when tab becomes hidden/visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Tab is hidden, start a shorter timeout
        logger.debug(' InactivityTracker: Tab hidden, monitoring session');
      } else {
        // Tab is visible again, reset the timer
        logger.debug(' InactivityTracker: Tab visible, resetting timer');
        resetTimer();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up event listeners on component unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      events.forEach(event => {
        window.removeEventListener(event, activityHandler);
      });
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [logout]);

  return null; // This component doesn't render anything
};

export default InactivityTracker;